import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { formatNumber } from '@utils/utils.ts';
import AdminCreditUsageFilters from '@features/admin-credit-usage/containers/AdminCreditUsageFilters';
import useAdminCreditUsageFilters from '@features/admin-credit-usage/hooks/useAdminCreditUsageFilters.tsx';
import AdminCreditUsageTotals from '@features/admin-credit-usage/containers/AdminCreditUsageTotals';

import useAdminCreditUsageData from '@features/admin-credit-usage/hooks/useAdminCreditUsageData.tsx';
import AdminCreditUsageTotalTable from '@features/admin-credit-usage/containers/AdminCreditUsageTotalTable';
import { AdminCreditUsageTotalTableRow } from '@features/admin-credit-usage/types';
import styles from './index.module.scss';
// Temporarily disabled Insights to reduce build memory - will re-enable after backend is ready
// import { lazy, Suspense } from 'react';
// import { CircularProgress } from '@mui/material';
// const AdminCreditUsageInsights = lazy(() => import('@features/admin-credit-usage/containers/AdminCreditUsageInsights'));

function formatCategoryLabel(
  interval: string | number,
  dateStr: string
): string {
  const date = dayjs(dateStr);
  switch (interval) {
    case 'day':
    case 'week':
      return date.format('MMM D');
    case 'month':
      return date.format('MMM YYYY');
    case 'quarter': {
      const q = Math.ceil((date.month() + 1) / 3);
      return `Q${q} ${date.year()}`;
    }
    case 'year':
      return date.format('YYYY');
    default:
      return date.format('MMM D');
  }
}

function AdminCreditUsage() {
  const {
    selectedCreditType,
    selectedInterval,
    startDate,
    endDate,
    setTotalTableData,
  } = useAdminCreditUsageFilters();

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({
    chart: {
      height: 500,
      type: 'line',
    },
    title: {
      text: 'Credit Usage',
    },
    xAxis: {
      categories: [],
      title: { text: 'Month' },
    },
    yAxis: [
      {
        title: { text: 'Credits' },
        labels: {
          formatter() {
            return Highcharts.numberFormat(this.value as number, 0);
          },
        },
      },
      {
        title: { text: 'Users' },
        opposite: true,
        labels: {
          formatter() {
            return Highcharts.numberFormat(this.value as number, 0);
          },
        },
      },
    ] as Highcharts.YAxisOptions[],
    tooltip: {
      useHTML: true,
      formatter() {
        const value = this.y as number;
        const formattedValue = Highcharts.numberFormat(value, 0);

        return `
          <b>${this.key}</b><br/>
          <span style="color:${this.color}">●</span> ${this.series.name}: ${formattedValue}<br/>
        `;
      },
    },
    series: [],
  });

  const { data, isLoading } = useAdminCreditUsageData({
    creditType: String(selectedCreditType.value),
    interval: String(selectedInterval.value),
    startDate,
    endDate,
  });

  useEffect(() => {
    if (isLoading || !data || data.length === 0) return;

    const categories = data.map((item) =>
      formatCategoryLabel(selectedInterval.value, item.date)
    );

    const totalTableRows: AdminCreditUsageTotalTableRow[] = [];

    const totalCreditsData = data.map((v) => v.total_credits_consumed);
    const caseCreditsData = data.map((v) => v.case_credits);
    const demandLetterCreditsData = data.map((v) => v.demand_letter_credits);
    const depositionCreditsData = data.map((v) => v.deposition_credits);
    const usersData = data.map((v) => v.total_users);

    totalTableRows.push({
      name: 'Total Credits',
      avg:
        totalCreditsData.reduce((acc, v) => acc + v, 0) /
        totalCreditsData.length,
      values: totalCreditsData,
      total: totalCreditsData.reduce((acc, v) => acc + v, 0),
      formatFunction: (v: number) => formatNumber(v),
    });

    totalTableRows.push({
      name: 'Case Credits',
      avg:
        caseCreditsData.reduce((acc, v) => acc + v, 0) / caseCreditsData.length,
      values: caseCreditsData,
      total: caseCreditsData.reduce((acc, v) => acc + v, 0),
      formatFunction: (v: number) => formatNumber(v),
    });

    totalTableRows.push({
      name: 'Demand Letter Credits',
      avg:
        demandLetterCreditsData.reduce((acc, v) => acc + v, 0) /
        demandLetterCreditsData.length,
      values: demandLetterCreditsData,
      total: demandLetterCreditsData.reduce((acc, v) => acc + v, 0),
      formatFunction: (v: number) => formatNumber(v),
    });

    totalTableRows.push({
      name: 'Deposition Credits',
      avg:
        depositionCreditsData.reduce((acc, v) => acc + v, 0) /
        depositionCreditsData.length,
      values: depositionCreditsData,
      total: depositionCreditsData.reduce((acc, v) => acc + v, 0),
      formatFunction: (v: number) => formatNumber(v),
    });

    totalTableRows.push({
      name: 'Active Users',
      avg: usersData.reduce((acc, v) => acc + v, 0) / usersData.length,
      values: usersData,
      total: usersData.reduce((acc, v) => acc + v, 0),
      formatFunction: (v: number) => formatNumber(v),
    });

    setTotalTableData({
      headers: categories,
      rows: totalTableRows,
    });

    const series: Highcharts.SeriesOptionsType[] = [
      {
        name: 'Total Credits',
        data: totalCreditsData,
        yAxis: 0,
        type: 'line',
        dataLabels: {
          enabled: true,
          formatter() {
            return Highcharts.numberFormat(this.y as number, 0);
          },
        },
      } as Highcharts.SeriesOptionsType,
      {
        name: 'Case Credits',
        data: caseCreditsData,
        yAxis: 0,
        type: 'line',
        dataLabels: {
          enabled: true,
          formatter() {
            return Highcharts.numberFormat(this.y as number, 0);
          },
        },
      } as Highcharts.SeriesOptionsType,
      {
        name: 'Demand Letter Credits',
        data: demandLetterCreditsData,
        yAxis: 0,
        type: 'line',
        dataLabels: {
          enabled: true,
          formatter() {
            return Highcharts.numberFormat(this.y as number, 0);
          },
        },
      } as Highcharts.SeriesOptionsType,
      {
        name: 'Deposition Credits',
        data: depositionCreditsData,
        yAxis: 0,
        type: 'line',
        dataLabels: {
          enabled: true,
          formatter() {
            return Highcharts.numberFormat(this.y as number, 0);
          },
        },
      } as Highcharts.SeriesOptionsType,
      {
        name: 'Active Users',
        data: usersData,
        yAxis: 1,
        type: 'line',
        dataLabels: {
          enabled: true,
          formatter() {
            return Highcharts.numberFormat(this.y as number, 0);
          },
        },
      } as Highcharts.SeriesOptionsType,
    ];

    setChartOptions((prevOptions) => ({
      ...prevOptions,
      xAxis: {
        ...prevOptions.xAxis,
        categories,
        title: { text: selectedInterval.label },
      },
      series,
    }));
  }, [data, isLoading, selectedInterval.value, selectedInterval.label]);

  return (
    <div className={styles.chartContainer}>
      <AdminCreditUsageFilters />
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      <AdminCreditUsageTotalTable />
      <AdminCreditUsageTotals />
      {/* Temporarily disabled Insights to reduce build memory - will re-enable after backend is ready */}
      {/* <Suspense fallback={<CircularProgress />}>
        <AdminCreditUsageInsights />
      </Suspense> */}
    </div>
  );
}

export default AdminCreditUsage;
